import { useDatasets } from 'api/dataset/dataset.api'
import { DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS } from 'api/dataset/dataset.constants'
import { useUiConfig } from 'api/uiConfig/uiConfig.api'
import type { AppliedSettingSeries, OptJobRun } from 'helpers/appliedSettingValues.helper/appliedSettingValues.types'
import {
  buildDatasetInstructions,
  narrowToSeries,
} from 'helpers/appliedSettingValues.helper/datasetInstructions.helper'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import Datetime from 'utils/datetime/datetime'

const BASE_CONFIG = 'base_config'

type AppliedSettingValueDatasetsRequest = {
  selection: AppliedSettingSeries[]
  runs: OptJobRun[]
  overrideAlias: UiConfigAliases
  period: { startTime: ISODateTime; endTime: ISODateTime }
  priorityLevels: number[]
}

type AppliedSettingValueDatasets = {
  status: 'pending' | 'error' | 'ready'
  isFetching: boolean
  datasets: Dataset[]
  retry: () => void
}

function withPeriodAliases(
  overrideAlias: UiConfigAliases,
  period: AppliedSettingValueDatasetsRequest['period']
): UiConfigAliases {
  return {
    ...overrideAlias,
    start_time: Datetime.toISOString(period.startTime),
    offset_start_time: 0,
    end_time: Datetime.toISOString(period.endTime),
    offset_end_time: 0,
  }
}

/**
 * The data behind the selected Applied setting values series, in one query: BaseConfig's dataset once per
 * opt-job run and the selected settings' applied values, narrowed to the selection.
 */
export function useAppliedSettingValueDatasets({
  selection,
  runs,
  overrideAlias,
  period,
  priorityLevels,
}: AppliedSettingValueDatasetsRequest): AppliedSettingValueDatasets {
  const { activeSystem } = useAuth()
  const { data: uiConfigs, isPending: isBaseConfigPending } = useUiConfig(activeSystem?.id, BASE_CONFIG)
  const baseConfig = uiConfigs?.find(({ component }) => component === BASE_CONFIG)

  const { instructions, onlyReturnIds } =
    baseConfig && activeSystem
      ? narrowToSeries(
          buildDatasetInstructions(baseConfig, selection, runs, overrideAlias, priorityLevels, activeSystem.id),
          selection
        )
      : { instructions: [], onlyReturnIds: [] }

  const query = useDatasets(
    baseConfig?.id ?? 0,
    baseConfig?.version ?? 0,
    instructions,
    period.startTime,
    period.endTime,
    {
      datasetRefreshToken: DATASET_FOR_UI_CONFIG_REFRESH_APPLIED_SETTINGS,
      overrideAlias: withPeriodAliases(overrideAlias, period),
      onlyReturnIds,
      keepPreviousData: true,
    }
  )

  // Until BaseConfig arrives the query runs with no instructions and settles on an empty list, which
  // keepPreviousData then shows while the real request loads; there is nothing on screen to keep yet.
  const isWaitingForFirstData = isBaseConfigPending || (query.isPlaceholderData && !query.data?.length)

  return {
    status: query.isPending || isWaitingForFirstData ? 'pending' : query.isError ? 'error' : 'ready',
    isFetching: query.isFetching,
    datasets: query.data ?? [],
    retry: () => {
      query.refetch()
    },
  }
}
